import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider.js';
import { sectionOf, sectionsFor, activeItemTo, parseRoles, type MenuItem } from '../menu.js';
import { ADMIN_ACCOUNTS_PATH, SETTINGS_SECTION_KEY, canSeeSettingsItem } from '../lib/settingsAccess.js';
import { ErrorBoundary } from './ErrorBoundary.js';
import { NewVersionBanner } from './NewVersionBanner.js';
import { SectionTitle, sectionBrand } from './SectionTitle.js';
import { ThemeToggle } from './ThemeToggle.js';

/**
 * 상단 메뉴.
 *
 * 원본 home.php 는 첫 화면에서만 메뉴 전체를 보여주고, 화면에 들어가면
 * 그 화면이 속한 묶음의 메뉴만 위에 뒀다 (lottery_top_nav.php 등).
 * 여기서도 같게 한다 - 전체를 늘어놓으면 지금 어디에 있는지 알기 어렵다.
 */

function NavItem({ item, active }: { item: MenuItem; active: boolean }) {
  if (item.to) {
    // isActive(NavLink 기본 prefix 매칭)에 안 맡기고 activeItemTo 가 고른
    // "더 긴 경로" 결과를 그대로 쓴다 - 안 그러면 접두어가 겹치는 두 항목이
    // 동시에 활성화된다(menu.ts 의 activeItemTo 주석 참고).
    return (
      <NavLink to={item.to} end={false} className={active ? 'navlink navlink--active' : 'navlink'}>
        {item.name}
      </NavLink>
    );
  }

  if (item.disabled) {
    return (
      <span className="navlink navlink--off" title={item.disabled}>
        {item.name}
      </span>
    );
  }

  if (item.external) {
    return (
      <a className="navlink" href={item.external} target="_blank" rel="noreferrer">
        {item.name}
      </a>
    );
  }

  return (
    <a className="navlink navlink--legacy" href={item.legacy} title="아직 이관되지 않은 화면">
      {item.name}
      <span className="navlink__badge">PHP</span>
    </a>
  );
}

export function Layout() {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const section = sectionOf(pathname);
  const brand = section ? sectionBrand(section) : undefined;
  const activeTo = section ? activeItemTo(pathname, section.items) : undefined;
  // '설정'(관리자 계정·화이트 IP)은 섹션 시스템 밖이다 - 화면 단위로 따로
  // 확인한다(settingsAccess.ts 참고). 관리자 계정은 최고 관리자 권한이 있으면
  // 누구나, 화이트 IP 는 xadmin 한 명만 본다. 한때 sec:settings 로 개별 부여할
  // 수 있었지만 최고 관리자가 아닌 계정도 다른 관리자 계정·화이트 IP 를 건드릴
  // 수 있게 돼 2026-09-03 에 없앴다.
  const roles = parseRoles(user?.authtype);
  const isSettingsSection = section?.key === SETTINGS_SECTION_KEY;
  // 지금 경로가 메뉴 섹션에 속하는데(section 있음) 이 계정에 그 섹션이 없으면
  // 링크를 안 보여주는 것만으로는 부족하다 - URL 을 직접 입력하면 여전히 화면이
  // 열렸다. 섹션이 없는 경로(sectionOf 가 못 찾는 유틸리티 화면 등)는 그대로 둔다.
  const allowed =
    !section ||
    (isSettingsSection
      ? canSeeSettingsItem(roles, user?.userid, activeTo)
      : sectionsFor(user?.authtype).some((s) => s.key === section.key));
  const hasSettingsShortcut = canSeeSettingsItem(roles, user?.userid, ADMIN_ACCOUNTS_PATH);

  return (
    <div className="shell">
      <NewVersionBanner />
      {/* 랜덤박스 두 섹션은 상단바를 섹션 색으로 물들인다(SectionTitle.tsx 참고). */}
      <header className={brand ? `topbar topbar--${brand}` : 'topbar'}>
        <div className="topbar__brand">
          <Link to="/" className="topbar__logo" title="전체 메뉴">
            xadmin
          </Link>
          {section && (
            <span className="topbar__section">
              <SectionTitle section={section} />
            </span>
          )}
        </div>

        {/* 지금 화면이 속한 섹션의 메뉴만 보여준다. 전체는 첫 화면에 있다. */}
        <nav className="topbar__nav" aria-label={section ? `${section.title} 메뉴` : '메뉴'}>
          {section?.items
            .filter((item) => !item.hideFromTopNav)
            .filter((item) => !isSettingsSection || canSeeSettingsItem(roles, user?.userid, item.to))
            .map((item) => (
              <NavItem key={item.name} item={item} active={item.to !== undefined && item.to === activeTo} />
            ))}
        </nav>

        <div className="topbar__user">
          <ThemeToggle />
          <a
            href="https://product.bbnn.kr/"
            target="_blank"
            rel="noreferrer"
            className="btn btn--ghost btn--sm"
          >
            발주관리시스템
          </a>
          <Link to="/" className="btn btn--ghost btn--sm">
            전체 메뉴
          </Link>
          {/* menu.ts '설정' 섹션과 같은 화면으로 가는 바로가기다. 최고 관리자
              권한이 있으면 보인다(settingsAccess.ts 참고). */}
          {hasSettingsShortcut && (
            <Link to={ADMIN_ACCOUNTS_PATH} className="btn btn--ghost btn--sm">
              관리자 계정
            </Link>
          )}
          <span className="topbar__username">{user?.name ?? user?.userid}</span>
          <button type="button" className="btn btn--ghost" onClick={() => void logout()}>
            로그아웃
          </button>
        </div>
      </header>

      <main className="content">
        {allowed ? (
          // 화면 하나가 렌더 중 던져도 메뉴/레이아웃은 살아 있어야 다른 화면으로
          // 빠져나갈 수 있다. resetKey 를 경로로 둬서 다음 화면으로 옮기면 저절로
          // 복구된다.
          <ErrorBoundary resetKey={pathname}>
            <Outlet />
          </ErrorBoundary>
        ) : (
          <div className="panel">
            <p className="panel__note">이 화면({section!.title})에 대한 권한이 없습니다.</p>
            <Link to="/" className="btn btn--ghost btn--sm">
              전체 메뉴로
            </Link>
          </div>
        )}
      </main>
    </div>
  );
}
